import { SecondaryButton, typography } from "@joor/design-system";
import { useFlags } from "launchdarkly-react-client-sdk";
import React from "react";
import styled from "styled-components/macro";

import { ToClassicButton } from "ui/Button";
import { logger } from "lib/Datadog";
import ErrorBoundary from "lib/ErrorBoundary";
import { errorMapToMessages, mapError } from "lib/ErrorMapping";

const selectTextInNode = (node: HTMLElement | null) => {
  const selection = window.getSelection();
  if (node && selection) {
    const range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
    return node.textContent ?? undefined;
  }
  return undefined;
};
const StyledErrorTextSectionId = "StyledErrorTextSectionId";
const StyledErrorTextSection = styled.pre`
  align-content: start;
  background: var(--color-neutral-100);
  border-radius: 6px;
  border: 1px solid var(--color-neutral-200);
  color: var(--color-primary-300);
  padding: 18px;
  text-align: start;
  overflow: scroll;
  max-height: 400px;
  white-space: pre-wrap;
`;
const StyleErrorDetailsSection = styled.div`
  display: flex;
  justify-content: space-evenly;
  padding: 16px 0;
`;

const SimpleCollapsable = styled.div<{ isOpen: boolean }>`
  overflow: hidden;
  height: ${({ isOpen }) => (isOpen ? "auto" : 0)};
`;

const ErrorDetailsComponent = ({ error }: { error: Error }) => {
  const [showError, setShowError] = React.useState(false);
  return (
    <>
      <StyleErrorDetailsSection>
        <SecondaryButton
          data-testid="btn-toggle-error-details"
          onClick={() => setShowError(!showError)}
          iconName={showError ? "contract-vertical" : "expand-vertical"}
        >
          {showError ? "Hide error details" : "Show error details"}
        </SecondaryButton>
        <SecondaryButton
          data-testid="btn-copy-error-details"
          iconName={"copy"}
          onClick={async () => {
            const textInNode = selectTextInNode(
              document.getElementById(StyledErrorTextSectionId)
            );
            if (textInNode) {
              return navigator.clipboard.writeText(textInNode);
            }
          }}
        >
          Copy error details
        </SecondaryButton>
      </StyleErrorDetailsSection>
      <SimpleCollapsable isOpen={showError}>
        <StyledErrorTextSection id={StyledErrorTextSectionId}>
          {JSON.stringify(mapError(error), null, 4)}
        </StyledErrorTextSection>
      </SimpleCollapsable>
    </>
  );
};

const StyledWrapper = styled.div`
  ${typography.alpha.primaryActive.body2}
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledComponent = styled.div`
  align-items: center;
  justify-content: center;
  padding: 30px;
  text-align: center;
  width: 800px;
`;

const AppErrorComponent = ({ error }: { error: Error }) => {
  const { removeBackToClassicButton } = useFlags();
  logger.error(error.message, { errorDetailsBody: mapError(error) });

  const errorMessage = errorMapToMessages({ error });
  return (
    <StyledWrapper>
      <StyledComponent>
        <h1>{errorMessage.firstMessage}</h1>
        <h2>{errorMessage.secondMessage}</h2>
        <ErrorBoundary fallbackRender={() => null}>
          {!removeBackToClassicButton && (
            <>
              <h2>{errorMessage.thirdMessage}</h2>
              <div>
                <ToClassicButton />
              </div>
            </>
          )}
        </ErrorBoundary>
        <ErrorDetailsComponent error={error} />
      </StyledComponent>
    </StyledWrapper>
  );
};

export default AppErrorComponent;
